import React, { useState, useEffect, useCallback } from 'react';
import { Shield, FileText, Calendar, Search, Loader2, X, AlertTriangle, Eye } from 'lucide-react';
import LoginPromptModal from '../components/LoginPromptModal';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import '../styles/jurisprudence.css';

/**
 * Jurisprudence – Legal & policy desk for Ayurvedic surgical practice.
 * Single section: one list of regulations/guidelines/rulings fetched from API with paging.
 */
const DOCUMENT_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'guideline', label: 'Guideline' },
  { value: 'regulation', label: 'Regulation' },
  { value: 'ruling', label: 'Court Ruling' },
  { value: 'ethical', label: 'Ethical / Policy' },
];

const PAGE_SIZE = 10;

const Jurisprudence = () => {
  const { isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Modal State
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const fetchItems = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const type = selectedType === 'all' ? null : selectedType;
      const result = await apiService.getJurisprudencePage({
        page,
        pageSize: PAGE_SIZE,
        searchTerm: searchTerm.trim() || null,
        type,
      });

      if (append) {
        setItems((prev) => [...prev, ...result.items]);
      } else {
        setItems(result.items);
      }

      setTotalCount(result.totalCount);
      setHasMore(result.items.length === PAGE_SIZE && (page * PAGE_SIZE) < result.totalCount);
      setPageNumber(page);
    } catch (err) {
      setError(err.message || 'Failed to load documents');
      if (!append) {
        setItems([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchTerm, selectedType]);

  useEffect(() => {
    fetchItems(1, false);
  }, [fetchItems]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchItems(pageNumber + 1, true);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
  };

  const handleCardClick = (item) => {
    setSelectedDoc(item);
    setIsDetailModalOpen(true);
  };

  const handleReadOrDownload = (item) => {
    if (!isAuthenticated()) {
      setShowLoginPrompt(true);
      return;
    }

    const documentUrl = (item.documentUrl ?? item.DocumentUrl ?? '').trim();
    if (!documentUrl) {
      alert('Document not available');
      return;
    }

    // Always use absolute URL so the new tab hits the API host, not the current site
    const apiBase = (process.env.REACT_APP_API_URL || 'https://vedicapi.azurewebsites.net/api').replace(/\/api\/?$/, '') || 'https://vedicapi.azurewebsites.net';
    const fullUrl = documentUrl.startsWith('http')
      ? documentUrl
      : `${apiBase}${documentUrl.startsWith('/') ? '' : '/'}${documentUrl}`;
    window.open(fullUrl, '_blank');
  };

  const getTypeMeta = (type) => {
    switch (type) {
      case 'ruling':
        return { colorClass: 'border-ruling', badgeClass: 'badge-ruling', label: 'Court Ruling' };
      case 'regulation':
        return { colorClass: 'border-regulation', badgeClass: 'badge-regulation', label: 'Regulation' };
      case 'guideline':
        return { colorClass: 'border-guideline', badgeClass: 'badge-guideline', label: 'Guideline' };
      case 'ethical':
        return { colorClass: 'border-ethical', badgeClass: 'badge-ethical', label: 'Ethical / Policy' };
      default:
        return { colorClass: 'border-default', badgeClass: 'badge-default', label: type };
    }
  };

  // Load active compliance directive for top warning component
  const activeDirective = items.find(
    (item) => item.type === 'guideline' || item.type === 'regulation'
  );

  return (
    <div className="jurisprudence-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content text-center">
            <h1>
              <Shield size={44} className="hero-icon" />
              Surgical Jurisprudence & Policy
            </h1>
            <p>
              Legal framework, NCISM directives, and regulatory guidelines governing Ayurvedic surgical practices.
            </p>
            <div className="hero-badges">
              <span className="badge">NCISM Aligned</span>
              <span className="badge">Legal Desk</span>
              <span className="badge">Compliance Reference</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Body */}
      <section className="section section-legal" style={{ paddingTop: '0' }}>
        <div className="container">
          
          {/* Pinned Compliance Alert (Matches textbooks "Continue Reading" widget layout) */}
          {activeDirective && searchTerm === '' && selectedType === 'all' && (
            <div className="pinned-directive-alert">
              <div className="directive-alert-badge">
                <AlertTriangle size={15} />
                <span>Active compliance directive</span>
              </div>
              <div className="directive-alert-inner">
                <div className="directive-icon-side">
                  <Shield size={32} className="shield-glow" />
                </div>
                <div className="directive-details-side">
                  <h4>{activeDirective.title}</h4>
                  <p>{activeDirective.description || 'Regulatory updates required for all clinical surgery practitioners.'}</p>
                </div>
                <button
                  type="button"
                  className="btn btn-primary directive-btn"
                  onClick={() => handleCardClick(activeDirective)}
                >
                  Review Compliance
                </button>
              </div>
            </div>
          )}

          {/* Unified Filter Bar */}
          <div className="unified-filter-bar">
            {/* Search Input */}
            <div className="filter-search-wrapper">
              <Search className="filter-search-icon" size={18} />
              <input
                type="text"
                placeholder="Search regulations, legal acts, guidelines..."
                className="filter-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="filter-search-clear" onClick={() => setSearchTerm('')} title="Clear search">
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Type Filter Pills */}
            <div className="filter-pills-group">
              {DOCUMENT_TYPES.map((opt) => (
                <button
                  key={opt.value}
                  className={`filter-pill ${selectedType === opt.value ? 'active' : ''}`}
                  onClick={() => setSelectedType(opt.value)}
                >
                  {opt.label === 'All' ? 'All Publications' : opt.label}
                </button>
              ))}
            </div>

            {/* Reset option */}
            {(searchTerm || selectedType !== 'all') && (
              <div className="filter-selects-group">
                <button className="btn-filter-reset" onClick={resetFilters}>
                  Clear Filters
                </button>
              </div>
            )}
          </div>

          {/* Grid/List Results */}
          {loading && items.length === 0 ? (
            <div className="legal-empty">
              <Loader2 size={32} className="spinner" />
              <p>Loading legal documents...</p>
            </div>
          ) : error ? (
            <div className="legal-empty">
              <AlertTriangle size={32} style={{ color: '#ef4444' }} />
              <p>{error}</p>
              <button type="button" className="btn btn-outline btn-small" onClick={() => fetchItems(1, false)}>
                Retry Connection
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="legal-empty">
              <FileText size={32} />
              <p>No guidelines or regulations matched your criteria.</p>
              <button type="button" className="btn btn-primary btn-small" onClick={resetFilters} style={{ marginTop: '1rem' }}>
                Reset Filters
              </button>
            </div>
          ) : (
            <>
              <div className="legal-grid">
                {items.map((item) => {
                  const meta = getTypeMeta(item.type);
                  return (
                    <article 
                      key={item.id} 
                      className={`legal-card card ${meta.colorClass}`}
                      onClick={() => handleCardClick(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="legal-card-header">
                        <span className={`legal-type-badge ${meta.badgeClass}`}>
                          {meta.label}
                        </span>
                        {item.date && (
                          <span className="legal-date">
                            <Calendar size={14} />
                            {item.date}
                          </span>
                        )}
                      </div>
                      <h3 className="legal-card-title">{item.title}</h3>
                      {item.description && <p className="legal-card-desc">{item.description}</p>}
                      <div className="legal-card-footer">
                        <button
                          type="button"
                          className="btn btn-outline btn-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick(item);
                          }}
                        >
                          <Eye size={14} />
                          View Details
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>

              {hasMore && (
                <div className="load-more-container">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 size={16} className="spinner" style={{ marginRight: '0.5rem' }} />
                        Loading...
                      </>
                    ) : (
                      'Load More Publications'
                    )}
                  </button>
                  <p className="load-more-count">
                    Showing {items.length} of {totalCount} compliance directives
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Interactive Dossier Detail Modal */}
      {isDetailModalOpen && selectedDoc && (
        <div className="dossier-modal-overlay" onClick={() => setIsDetailModalOpen(false)}>
          <div className="dossier-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="dossier-modal-header">
              <div className="dossier-header-badge-row">
                <span className={`legal-type-badge ${getTypeMeta(selectedDoc.type).badgeClass}`}>
                  {getTypeMeta(selectedDoc.type).label}
                </span>
                {selectedDoc.date && (
                  <span className="legal-date">
                    <Calendar size={14} />
                    Published: {selectedDoc.date}
                  </span>
                )}
              </div>
              <h3>{selectedDoc.title}</h3>
              <button className="dossier-modal-close" onClick={() => setIsDetailModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="dossier-modal-body">
              <div className="dossier-section-title">
                <FileText size={18} />
                <h4>Executive Summary & Background</h4>
              </div>
              <p className="dossier-doc-desc">
                {selectedDoc.description || 'No description available for this legal publication.'}
              </p>

              <div className="highlight-box warning-box">
                <h5>
                  <AlertTriangle size={16} />
                  Compliance & Applicability Checklist
                </h5>
                <ul>
                  <li><strong>Status:</strong> Active & Enforced by NCISM Board of Ethics.</li>
                  <li><strong>Audience:</strong> Practitioners and Post-Graduate Scholars of Shalya Tantra (Surgery).</li>
                  <li><strong>Requirement:</strong> Verify registration and logbook documentation regularly.</li>
                </ul>
              </div>

              <div className="modal-metadata-footer">
                <div className="meta-footer-item">
                  <strong>Reference ID</strong>
                  <span>NCISM-JUR-{selectedDoc.id || 'N/A'}</span>
                </div>
                <div className="meta-footer-item">
                  <strong>Jurisdiction</strong>
                  <span>National Commission for Indian System of Medicine</span>
                </div>
                <div className="meta-footer-item">
                  <strong>Category</strong>
                  <span>Surgical Ethics & Practice</span>
                </div>
              </div>
            </div>

            <div className="dossier-modal-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setIsDetailModalOpen(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  handleReadOrDownload(selectedDoc);
                  setIsDetailModalOpen(false);
                }}
              >
                <FileText size={14} style={{ marginRight: '0.4rem' }} />
                View / Download Official PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign in to read or download"
        message="Create a free account or sign in to access full documents and downloads."
      />
    </div>
  );
};

export default Jurisprudence;
